import { useToastr } from '@farmshare/ui-components';
import { compact, keyBy, map, omit } from 'lodash';
import { useCallback, useMemo, useRef, useState } from 'react';

import { useProcessorSchedulingTagsUpdateMutation } from '../../lib/graphql';
import { userFacingError } from 'lib/user-facing-error';

import type { JobTagDisplay } from './job-tag-icons';
import { applyPendingTagToggles } from './apply-pending-tag-toggles';
import { useJobTags } from './use-job-tags';

interface UseJobTagToggleArgs {
  jobId: string | null | undefined;
  /** The job's tags as the server last reported them. */
  tags: readonly (JobTagDisplay | null | undefined)[] | null | undefined;
}

/**
 * Puts tags on and takes them off ONE job, one tag at a time.
 *
 * Each toggle is sent as an `addTags` / `removeTags` delta that the server
 * applies atomically — never as the job's whole next list. A list is built
 * from whatever this client last saw, so two quick clicks used to overwrite
 * each other and the first tag was lost.
 *
 * Two things make the UI honest about that:
 *
 * - The click shows IMMEDIATELY. Unanswered toggles are held as an overlay
 *   (`pending`) on top of the server's tags, and each entry is dropped when
 *   its request settles — by then the mutation's response has written the
 *   job's stored tags into the Apollo cache, so the chip either stays (it
 *   saved) or snaps back (it was refused).
 * - Requests go out one at a time, in click order. The server no longer needs
 *   that for correctness, but responses landing out of order would leave the
 *   cache showing an older answer than the last one given.
 * @param root0
 * @param root0.jobId the job being tagged.
 * @param root0.tags the job's tags from the server relation.
 * @returns the tags to draw (server state plus unanswered toggles), their
 *   ids, and `toggle`.
 */
export function useJobTagToggle({ jobId, tags }: UseJobTagToggleArgs) {
  const { push } = useToastr();
  const { tags: vendorTags } = useJobTags();
  const [updateTags] = useProcessorSchedulingTagsUpdateMutation();

  /** tagId → the state the user asked for, while its request is in flight. */
  const [pending, setPending] = useState<Record<string, boolean>>({});
  const queue = useRef<Promise<void>>(Promise.resolve());
  /** Latest toggle per tag, so an older request can't clear a newer overlay. */
  const latestToggle = useRef<Record<string, number>>({});

  const toggle = useCallback(
    (tagId: string, selected: boolean) => {
      if (!jobId) return;

      const sequence = (latestToggle.current[tagId] ?? 0) + 1;
      latestToggle.current[tagId] = sequence;
      setPending((previous) => ({ ...previous, [tagId]: selected }));

      queue.current = queue.current.then(async () => {
        try {
          await updateTags({
            variables: {
              processorSchedulingId: jobId,
              addTags: selected ? [tagId] : [],
              removeTags: selected ? [] : [tagId],
            },
          });
        } catch (err) {
          push({
            title: "Couldn't update the job's tags",
            body: userFacingError(err, 'The tags haven’t changed. Try again.'),
            bg: 'danger',
            delay: 4000,
          });
        } finally {
          if (latestToggle.current[tagId] === sequence) {
            setPending((previous) => omit(previous, tagId));
          }
        }
      });
    },
    [jobId, updateTags, push],
  );

  // In the order the tags were put on, which is the order the job stores
  // them in. A pending add goes on the end, where the server will append it,
  // so a tag lands where it will stay rather than jumping when the answer
  // comes back — including a tag taken off and put straight back, which the
  // server re-appends (see `applyPendingTagToggles`).
  const displayTags = useMemo(
    () =>
      applyPendingTagToggles(compact(tags), pending, keyBy(vendorTags, '_id')),
    [tags, pending, vendorTags],
  );

  const tagIds = useMemo(() => map(displayTags, '_id'), [displayTags]);

  return { tags: displayTags, tagIds, toggle };
}
